/**
 * Beds, baths and square footage, rendered so that "we don't know" never reads as
 * a measurement.
 *
 * WHY. The feed sends 0 for absent, and the UI interpolated it directly:
 * `{listing.beds} bd · {formatBaths(listing.baths)} ba · {formatSqft(listing.sqft)}`
 * gives "0 bd · 0 ba · 0 sqft" on the card, in the map popup, on the detail page's
 * key-stats row and in the compare table. Land and mobile homes are both real
 * `PropertyType`s in this corpus, and a parcel of land does not have zero bedrooms —
 * it has none, which is a different statement. Printing the sentinel as a fact is
 * the same defect as rendering a failed fetch as "there are none": a gap in what we
 * know, dressed up as knowledge.
 *
 * Each formatter returns `null` for "nothing truthful to say", and `factLine` drops
 * the nulls, so a missing middle fact cannot leave a dangling separator.
 */

/** A value the feed actually carried. 0, null and undefined all mean "absent". */
function known(value: number | null | undefined): number | null {
  // `(x ?? 0) > 0` rather than `x &&` — a falsy 0 is exactly the case in play here.
  return (value ?? 0) > 0 ? (value as number) : null;
}

export function formatBedsFact(beds: number | null | undefined): string | null {
  const n = known(beds);
  return n === null ? null : `${n} bd`;
}

export function formatBathsFact(baths: number | null | undefined): string | null {
  const n = known(baths);
  if (n === null) return null;
  // Whole numbers stay whole; halves keep their half. "2.5 ba" is the range that
  // separates Bay Area townhomes and must not round away.
  return `${n % 1 === 0 ? n.toString() : n.toFixed(1)} ba`;
}

export function formatSqftFact(sqft: number | null | undefined): string | null {
  const n = known(sqft);
  return n === null ? null : `${n.toLocaleString()} sqft`;
}

/** The three facts, separated by the app's middle dot, omitting whatever is absent. */
export function factLine(listing: {
  beds?: number | null;
  baths?: number | null;
  sqft?: number | null;
}): string {
  return [
    formatBedsFact(listing.beds),
    formatBathsFact(listing.baths),
    formatSqftFact(listing.sqft),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}
